/**
 * TopSharePresetCard — surfaces the single best-converting Share &
 * Track preset for the entire event campaign. Lives on the Event
 * Dashboard overview so organizers can see — at a glance — which
 * channel is actually driving sales without digging into the
 * Widget Performance table.
 *
 * States:
 *  - loading: subtle skeleton
 *  - empty:   friendly "share with one of your channels" nudge
 *  - found:   highlight card with channel, conversions, CVR and
 *             a quick-copy share-link button
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import {
  TrendingUp, Sparkles, Copy, Check, ArrowRight, Trophy,
  Mail, Instagram, Facebook, Twitter, Music2, MessageSquare, Users, Link as LinkIcon,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Same icon mapping as ShareTrackTab — kept inline to avoid coupling.
const renderChannelIcon = (channel, className) => {
  const c = (channel || '').toLowerCase();
  if (c.includes('newsletter') || c.includes('email')) return <Mail className={className} />;
  if (c.includes('instagram')) return <Instagram className={className} />;
  if (c.includes('facebook')) return <Facebook className={className} />;
  if (c.includes('twitter') || c === 'x' || c.includes(' x ') || c.startsWith('x ')) return <Twitter className={className} />;
  if (c.includes('tiktok')) return <Music2 className={className} />;
  if (c.includes('sms') || c.includes('text')) return <MessageSquare className={className} />;
  if (c.includes('partner')) return <Users className={className} />;
  return <LinkIcon className={className} />;
};

export default function TopSharePresetCard({ eventId }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API}/events/${eventId}/share-presets/top-performer`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setData(res.data);
    } catch {
      // Silent fail — this is an enhancement card, not a blocker.
      setData({ found: false, reason: 'error' });
    } finally {
      setLoading(false);
    }
  }, [eventId, token]);

  useEffect(() => { if (eventId) load(); }, [eventId, load]);

  const copyShareLink = async () => {
    if (!data?.preset?.share_url) return;
    const text = data.preset.share_url;

    let success = false;
    if (navigator.clipboard?.writeText) {
      try { await navigator.clipboard.writeText(text); success = true; }
      catch { success = false; }
    }
    if (!success) {
      // Legacy fallback for iframed previews where Clipboard API is blocked.
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed'; ta.style.top = '0'; ta.style.left = '0'; ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select(); ta.setSelectionRange(0, ta.value.length);
      try { success = document.execCommand('copy'); } catch { success = false; }
      document.body.removeChild(ta);
    }

    if (success) {
      setCopied(true);
      toast.success('Share link copied!');
      setTimeout(() => setCopied(false), 1800);
    } else {
      toast.error('Copy failed — open Share & Track tab');
    }
  };

  if (loading) {
    return (
      <Card className="bg-card border-border" data-testid="top-preset-card-loading">
        <CardContent className="p-4">
          <div className="h-5 w-40 rounded bg-muted/40 mb-3 animate-pulse" />
          <div className="h-3 w-64 rounded bg-muted/30 animate-pulse" />
        </CardContent>
      </Card>
    );
  }

  // Empty state — no conversions tracked yet.
  if (!data?.found) {
    return (
      <Card
        className="bg-gradient-to-br from-slate-900/50 to-slate-900/20 border-border"
        data-testid="top-preset-card-empty"
      >
        <CardContent className="p-4 flex items-start gap-3">
          <div className="h-9 w-9 rounded-lg bg-indigo-500/15 flex items-center justify-center shrink-0">
            <Sparkles className="h-4 w-4 text-indigo-300" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-foreground text-sm">No top channel yet</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Share your event with one of your pre-tagged channels and we&apos;ll
              surface the best-converting one right here.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate(`/admin/events/${eventId}/marketing?tab=share`)}
            data-testid="top-preset-open-share-empty"
          >
            Open Share &amp; Track
            <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Found state
  const p = data.preset || {};
  return (
    <Card
      className="bg-gradient-to-br from-amber-950/40 via-card to-card border-amber-700/40"
      data-testid="top-preset-card"
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {/* Trophy badge */}
          <div className="h-10 w-10 rounded-lg bg-amber-500/15 border border-amber-700/40 flex items-center justify-center shrink-0">
            <Trophy className="h-5 w-5 text-amber-300" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-amber-300/90">
                Top converting channel
              </span>
            </div>
            <div className="mt-1 flex items-center gap-2">
              {renderChannelIcon(p.channel, 'h-4 w-4 text-amber-200')}
              <h3
                className="font-bold text-foreground text-base truncate"
                data-testid="top-preset-channel"
              >
                {p.channel || 'Unknown channel'}
              </h3>
            </div>

            <p className="text-sm text-muted-foreground mt-2 leading-snug">
              Drove{' '}
              <span
                className="font-semibold text-foreground"
                data-testid="top-preset-conversions"
              >
                {data.conversions} sale{data.conversions === 1 ? '' : 's'}
              </span>
              {' '}·{' '}
              <span
                className="font-semibold text-emerald-300"
                data-testid="top-preset-cvr"
              >
                {data.cvr}% conversion
              </span>
              {data.total_tracked_presets > 1 && (
                <>
                  {' '}— your highest of {data.total_tracked_presets} tracked channels.
                </>
              )}
            </p>

            <div className="mt-2 flex items-center gap-3 text-[11px] text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <TrendingUp className="h-3 w-3" /> {data.impressions} views
              </span>
              <span>·</span>
              <span>{data.clicks} clicks</span>
              <span>·</span>
              <span data-testid="top-preset-revenue">
                ${data.revenue.toFixed(2)} revenue
              </span>
            </div>
          </div>

          {/* Quick actions */}
          <div className="flex flex-col gap-2 shrink-0">
            {p.share_url && (
              <Button
                size="sm"
                variant="outline"
                onClick={copyShareLink}
                data-testid="top-preset-copy-link"
              >
                {copied
                  ? <Check className="h-3.5 w-3.5 mr-1.5 text-emerald-400" />
                  : <Copy className="h-3.5 w-3.5 mr-1.5" />}
                {copied ? 'Copied' : 'Copy link'}
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => navigate(`/admin/events/${eventId}/marketing?tab=share`)}
              data-testid="top-preset-open-share"
              className="text-amber-300 hover:text-amber-200"
            >
              Manage <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
